// What happens when someone calls this number — drawn, not listed.
//
// WHERE IT WAS, AND WHAT WAS WRONG WITH IT. This card started life inside the customer page's
// hidden "account-wide" drawer, then moved onto the line's Routing tab as three labelled rows:
// inbound route, answers on, on the phone. Kevin, 2026-08-13: draw it —
//
//     Commio  →  PBX host  →  extension(s)  →  device(s)
//
// A call arrives at the carrier, lands on a host, reaches one or more extensions, and rings
// the phone bound to each. Three rows cannot show that ONE STEP FANS OUT, and that is not a
// cosmetic loss: it is exactly why the ring-group case read "answers on —" and "no phone"
// beside five bound handsets. A list has one slot per row, so a five-way branch had to render
// as either one arbitrary member or nothing, and it chose nothing.
//
// ON OVERVIEW, NOT ROUTING (Kevin, 2026-08-13). "What happens when someone dials this number"
// is the first thing an operator wants and it is not a routing edit. Routing keeps the
// controls that CHANGE the path; Overview shows the path.
//
// THE HONEST-VERIFICATION RULE IS THE WHOLE DESIGN HERE. Every leg is one of four states:
// working, wrong, not there yet, or NOT KNOWN. An unreachable host must grey a leg as unknown
// and must never draw it as absent — `destByDid` comes back empty from a host that did not
// answer exactly as it does from a host with no inbound route, and rendering the first as the
// second tells an operator to rebuild a line that is already fine. `callPathModel` is the one
// place that distinction is made, and it is exported so a test can hold it to it.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

const KIND_LABEL = {
  extension: 'an extension',
  'ring-group': 'a ring group',
  queue: 'a queue',
  'time-condition': 'a time condition',
  ivr: 'an auto attendant',
  conference: 'a conference',
  announcement: 'an announcement',
  terminate: 'hang up',
  'day-night': 'a day/night switch',
  other: 'something Verso does not recognise',
  unknown: 'something Verso does not recognise',
};

const STRATEGY_LABEL = {
  ringall: 'all together',
  hunt: 'one after another',
  memoryhunt: 'one, then two, then three',
  'ringall-prim': 'all together, primary first',
  firstavailable: 'the first one free',
  firstnotonphone: 'the first one not on a call',
};

/**
 * The whole path as data, so the drawing has no judgement of its own.
 *
 * Takes the four things the page already has — the line, the subscriber's routing read, this
 * line's ringing extensions, and the registration map — and returns one object per leg with
 * its state. Everything that decides between "not there" and "we could not look" happens
 * here.
 */
export function callPathModel({ line, routing = null, rings = [], regs = null, calix = null }) {
  const digits = String(line.number || '').replace(/\D/g, '');
  // THE NUMBER *AND* THE HOST. Matching on the number alone attributes a route found on one
  // host to a line recorded on another — and the whole point of the first leg is to catch
  // exactly that disagreement, so the one case this drawing exists for was the one it got
  // wrong.
  //
  // Stone Mountain Lodge, 2026-08-20: its two DIDs are recorded on PBX-01, the carrier delivers
  // both to PBX-02, and PBX-02 is where the inbound routes actually live (SML Front Desk / SML
  // Office → time conditions; PBX-01 has neither, out of 153 routes). The subscriber's routing
  // read spans both hosts because its lines do, so this found PBX-02's route, hung it under a
  // node labelled PBX-01 and chipped "inbound route installed" — a green light on a host with
  // nothing on it, six inches from a pending item on the same page saying the PBX had no route
  // for the number. Two statements, one card, opposite claims.
  //
  // A line with no host recorded still takes whatever the read found: there is no host to
  // disagree with, and `agentId` falls back to the path's for exactly that reason.
  const pathsForNumber = (routing?.paths || []).filter(
    (p) => String(p.e164 || '').replace(/\D/g, '') === digits,
  );
  const path = (line.pbxAgentId
    ? pathsForNumber.find((p) => String(p.pbx?.agentId || '') === String(line.pbxAgentId))
    : pathsForNumber[0]) || null;
  const agentId = line.pbxAgentId || path?.pbx?.agentId || null;
  // Named per host: the routing read reports its failures per agent, so a customer whose
  // numbers span two PBXes can have one leg live and one unknown.
  const hostError = (routing?.agentErrors && agentId && routing.agentErrors[agentId]) || null;
  // Did we get an answer from the host at all? Not "was the request made".
  const asked = Boolean(routing) && !hostError && Boolean(path);

  // ---- The carrier. ----
  //
  // Verso already computes this comparison for pending changes: the DID owns PBX routing, so
  // a number whose carrier profile points at a different host than its line's is a number
  // whose inbound calls arrive at the wrong PBX — invisible until somebody calls. Same rule,
  // same words, drawn as the first leg.
  // WHICH HOST, AND ON WHICH PROFILE — two questions, and this leg was answering the wrong one.
  //
  // Kevin, 2026-08-19: "LyonsCommPBX is the same as pbx-01.lyonscomm.com" — the same BOX: profile
  // 12461 routes to 23.149.168.23, PBX-01's public address, while 45509 reaches the same host by
  // hostname. 128 of 164 numbers are on the first, so comparing profile IDS drew this leg in red for
  // five numbers in six, all of them answering their calls, with the words "the carrier routes it
  // somewhere other than PBX-01". That claim was simply false.
  //
  // AND THEY ARE STILL NOT INTERCHANGEABLE. Kevin, immediately after: "you shouldn't treat them as
  // the same even though they resolve to the same IP because there are routing differences between
  // them." So collapsing them into `ok` would trade a false alarm for a false all-clear. There are
  // FOUR answers, not two:
  //
  //   ok            — the carrier delivers on this host's own profile;
  //   other_profile — it reaches this host, by a different profile than the host's. Not an outage,
  //                   and not nothing: the profiles differ in how they route;
  //   bad           — it delivers to a DIFFERENT host of ours, or nowhere at all;
  //   unknown       — the profile cannot be placed, which is not evidence of anything.
  //
  // `carrierReachesHost` is resolved server-side by address (lib/carrierRoutes.js); which profile it
  // is stays an id comparison, because that is exactly what the distinction is.
  const reaches = line.carrierReachesHost ?? null;
  const onHostProfile = line.carrierRouteId != null
    && String(line.carrierRouteId) === String(line.hostRouteId ?? '');
  const carrier = {
    number: line.number || null,
    cnam: line.cnam || null,
    route: line.carrierRoute || null,
    state: line.carrierRouteId == null
      ? (line.provisioned ? 'bad' : 'unknown')
      : reaches === false ? 'bad'
        : onHostProfile ? 'ok'
          : reaches === true ? 'other_profile'
            // Placed nowhere we know: say so rather than accusing the carrier.
            : 'unknown',
  };
  carrier.note = carrier.state === 'ok'
    ? `the carrier sends it to ${line.pbxName || 'this host'}`
    : carrier.state === 'other_profile'
      // Kevin, 2026-08-19: "just keep the copy simple and show there is a mismatch between the Verso
      // config and the Commio and provide the option to re-build the line to correct it." The
      // explaining sentence went; the two values stayed, and the pending list carries the Apply.
      ? `Verso and Commio disagree: the carrier uses ${line.carrierRoute || 'another route'}`
        + `${line.hostRoute ? `, not ${line.hostRoute}` : ''}`
      : carrier.state === 'bad'
        ? (line.carrierRouteId == null
          ? 'the carrier has no route for this number, so the call reaches no PBX'
          : `the carrier delivers it to a different one of our hosts, not ${line.pbxName || 'this host'}`)
        : (line.carrierRouteId == null
          ? `${line.pbxName || 'this host'} has no carrier route recorded, so Verso cannot tell where calls to it go`
          : `the carrier sends it to ${line.carrierRoute || 'a route'}, which Verso cannot match to one of its hosts`);

  // ---- The host, and what it is told to do with the number. ----
  const dest = path?.destination || null;
  const host = {
    name: line.pbxName || line.pbxAgentId || null,
    agentId,
    error: hostError,
    destination: dest,
    // 'missing' is only ever claimed when the host actually answered.
    // `flow` is its own state. It is NOT `missing` — the host has a record for this number and
    // routes it; it simply does not decide by the destination on that record, so there is no raw
    // string to show. Folded into `missing`, the card told an operator "the call reaches the PBX
    // and finds nothing" about a number that rings.
    state: !asked ? 'unknown' : dest?.kind === 'flow' ? 'flow' : dest?.raw ? 'ok' : 'missing',
  };
  // WHERE THE ROUTE IS, when this host has not got it. "No inbound route anywhere" and "the
  // route is on the other host" send an operator to different places, and only the second is
  // true of a number mid-migration — Stone Mountain Lodge's two DIDs are recorded on PBX-01 and
  // routed, by the carrier and by FreePBX both, on PBX-02.
  const elsewhere = (path?.routedElsewhere || []).filter((r) => r.name || r.agentId);
  host.note = host.state === 'ok'
    ? `it hands the call to ${KIND_LABEL[dest.kind] || dest.kind}`
    : host.state === 'flow'
      ? 'it decides where this number goes by its call flow — Verso cannot see what answers it'
      : host.state === 'missing'
      ? (elsewhere.length
        ? `the inbound route for this number is on ${elsewhere.map((r) => r.name || r.agentId).join(' and ')}, not here`
        : 'the host has no inbound route for this number — the call reaches the PBX and finds nothing')
      // Short, because the banner above the drawing carries the reason and the host's own
      // words. Repeating them on the leg said the same sentence twice on one card.
      : hostError
        ? 'what it does with this call is not known'
        : `Verso has not read ${host.name || 'the host'} for this number`;

  // ---- The branch. ----
  //
  // Live when the host answered; otherwise Verso's intent, LABELLED as intent. Drawing the
  // intended fan-out on an unbuilt line is right — it is what a build would install — as long
  // as the diagram does not pass it off as what the host is doing.
  const liveKind = asked && dest?.raw ? dest.kind : null;
  const fan = {
    live: Boolean(liveKind),
    kind: liveKind === 'ring-group' ? 'ring-group'
      : liveKind === 'extension' ? 'direct'
        : liveKind ? 'flow'
          : (line.routeType === 'ring_group' || rings.length > 1) ? 'ring-group' : 'direct',
    groupNum: liveKind === 'ring-group' ? (dest.target || line.ringGroupNum) : line.ringGroupNum,
    flowLabel: liveKind && !['ring-group', 'extension'].includes(liveKind)
      ? (KIND_LABEL[liveKind] || liveKind) : null,
    flowTarget: liveKind && !['ring-group', 'extension'].includes(liveKind) ? dest.target : null,
    // WHICH flow, so the drawing can tell a menu Verso manages from a flow it cannot touch.
    flowKind: liveKind && !['ring-group', 'extension'].includes(liveKind) ? liveKind : null,
    strategy: STRATEGY_LABEL[line.ringStrategy] || line.ringStrategy || null,
    ringTime: line.ringTime ?? null,
  };

  // ---- The extensions, and the phone on each. ----
  //
  // A line with nothing in `line_extensions` still rings its own account — the fallback the
  // server applies everywhere else. Without it the ordinary single-extension line, which is
  // most of the fleet, would draw a host branching to nothing.
  const list = rings.length ? rings : (line.sipAccountId ? [{
    sipAccountId: line.sipAccountId,
    username: line.extension,
    displayNameOverride: line.extensionDisplayNameOverride,
    syncedAt: line.extensionSyncedAt,
    deviceMac: line.deviceMac,
    deviceName: line.deviceName,
    deviceModel: line.deviceModel,
    devicePort: line.devicePort,
    deviceSyncedAt: line.deviceSyncedAt,
  }] : []);

  // KEYED BY SIP ACCOUNT ID, not by extension number. The map from /sip-registrations looks
  // like it is keyed by extension and is not; reading it the obvious way returns absent for
  // every line, which renders as "not registered" and is not (2026-08-10).
  const map = regs?.registrations || null;
  const legs = list.map((e, i) => {
    const reg = map ? (map[String(e.sipAccountId)] ?? null) : null;
    return {
      sipAccountId: e.sipAccountId,
      username: e.username || null,
      name: e.displayNameOverride || line.label || line.subscriberName || null,
      ownName: Boolean(e.displayNameOverride),
      primary: i === 0,
      // An extension missing from the map is "we did not get an answer for it", which is not
      // the same as unregistered.
      registration: reg == null ? 'unknown' : reg.registered ? 'online' : 'offline',
      callState: reg?.state && reg.state !== 'Not in use' ? reg.state : null,
      pushed: Boolean(e.syncedAt),
      device: e.deviceMac ? {
        mac: e.deviceMac,
        label: e.deviceName || e.deviceModel || e.deviceMac,
        port: e.devicePort ?? null,
        // A Calix pulls its own config from the ONT side, so it has no push to be waiting
        // for. Reading `deviceSyncedAt` alone chipped "never given its config" on Carole
        // Carroll's line while the phone was registered and working (2026-08-17). The
        // worker states this — the page does not re-derive it.
        pushed: Boolean(e.deviceSyncedAt) || Boolean(e.deviceSelfProvisioning),
        selfProvisioning: Boolean(e.deviceSelfProvisioning),
        // The Calix serial. Carried so the node can match itself against the health read, which
        // answers per ROUTER — a subscriber with two would otherwise show one router's state on
        // both.
        fsan: e.deviceFsan ?? null,
      } : null,
    };
  });

  // ---- Nobody answers. ----
  //
  // FreePBX gives a mailbox to an extension, never to a group, so a number ringing five
  // phones takes its messages on the first one's box — the same rule the Voicemail tab draws
  // and the same one the build installs as the group's no-answer destination.
  //
  // AND THE HOST DECIDES WHETHER THERE IS A MAILBOX AT ALL. This asked only
  // `line.voicemailEnabled` — Verso's intent — so an extension FreePBX holds as `novm` still
  // got a mailbox drawn on the end of its call path. Honorable Harvest Tea, 2026-08-20: the
  // header a few inches above read "Voicemail off", correctly, because it consults the live
  // `mailboxEnabled`; the drawing said "if nobody answers → Mailbox 13038235507". One page,
  // two answers, and the drawing was the confident one.
  //
  // `mailboxEnabled` is the same tri-state the Voicemail tab reads: false is "the host has no
  // mailbox", null is "we did not ask". Null keeps drawing the mailbox, because that is what a
  // build would install — but flagged `live: false`, so the drawing can own that it is intent
  // rather than pass it off as the host's own answer.
  const hostHasMailbox = line.mailboxEnabled;
  const tail = (line.voicemailEnabled === false || hostHasMailbox === false)
    ? { kind: 'ringout', reason: hostHasMailbox === false && line.voicemailEnabled !== false ? 'no_mailbox' : 'switched_off' }
    : legs.length
      ? { kind: 'voicemail', mailbox: legs[0].username, live: hostHasMailbox === true }
      : { kind: 'none' };

  // THE ROUTER'S OWN STATE, on the device it belongs to.
  //
  // A Calix is not in GDMS and Verso never pushes it, so everything the other device nodes say —
  // "config pushed", a port, a registration — is either absent or borrowed for one of these. What
  // the router can say for itself comes from Calix Cloud, and until now the only place it was shown
  // was a button on the subscriber page (Kevin, 2026-08-21: show it here).
  //
  // MATCHED BY FSAN, not just "this leg is a Calix": the read answers for one router, and a
  // subscriber with two would otherwise have both nodes claiming the same state. When Verso has no
  // FSAN recorded for the device there is nothing to match on, so nothing is claimed.
  for (const leg of legs) {
    if (!leg.device?.selfProvisioning) continue;
    if (!calix || !calix.fsan || !leg.device.fsan) continue;
    if (String(calix.fsan).toUpperCase() !== String(leg.device.fsan).toUpperCase()) continue;
    leg.device.calix = {
      fsan: calix.fsan,
      // `error` and `health` are exclusive in the response. Rate limiting is called out because
      // it is temporary and an operator should be told to come back, not told the router is broken.
      state: calix.error ? (calix.rate_limited ? 'rate_limited' : 'error')
        : calix.health ? (calix.health.online ? 'online' : 'offline')
          : 'unknown',
      firmware: calix.health?.firmware ?? null,
      lastInform: calix.health?.lastInform ?? null,
      wanIp: calix.health?.wanIp ?? null,
      issues: calix.health?.issues?.length || 0,
      cached: calix.cached === true,
    };
  }

  return { carrier, host, fan, legs, tail, unreachable: hostError };
}

// The carrier leg's four states, in tone. `other_profile` is deliberately amber rather than green:
// the call lands on the right PBX by a profile whose routing differs from the host's own.
const CARRIER_TONE = {
  ok: 'ok',
  other_profile: 'warn',
  bad: 'bad',
  unknown: 'unknown',
};

const TONE = {
  ok: 'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-900',
  bad: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-600',
};

function Chip({ tone = 'unknown', children }) {
  return (
    <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium ${TONE[tone]}`}>
      {children}
    </span>
  );
}

const EDGE = {
  ok: 'border-gray-300',
  warn: 'border-amber-400',
  bad: 'border-red-400',
  unknown: 'border-dashed border-gray-300',
};

function Node({ title, sub, chips = [], tone = 'ok', mono = false, wide = false }) {
  return (
    <div
      className={`rounded-lg border bg-white px-3 py-2 text-center ${EDGE[tone]} ${wide ? 'w-full max-w-md' : 'w-full'}`}
    >
      <div className={`text-sm font-medium text-gray-900 ${mono ? 'font-mono' : ''}`}>{title}</div>
      {sub && <div className="mt-0.5 break-words text-xs text-gray-500">{sub}</div>}
      {chips.length > 0 && (
        <div className="mt-1 flex flex-wrap justify-center gap-1">{chips}</div>
      )}
    </div>
  );
}

// The vertical run between two nodes, with what happens along it written beside the line.
// Dashed when the state is unknown — the same visual grammar as the node borders.
function Leg({ label, tone = 'ok' }) {
  return (
    <div className="flex w-full items-center gap-3 py-1">
      <div className="flex w-full flex-col items-center">
        <div className={`h-6 border-l ${tone === 'unknown' ? 'border-dashed border-gray-300' : EDGE[tone]}`} />
        {label && (
          <div className={`px-2 text-center text-xs ${
            tone === 'bad' ? 'text-red-700' : tone === 'warn' ? 'text-amber-800'
              : tone === 'unknown' ? 'text-gray-500' : 'text-gray-600'
          }`}
          >
            {label}
          </div>
        )}
        <div className={`h-6 border-l ${tone === 'unknown' ? 'border-dashed border-gray-300' : EDGE[tone]}`} />
        <Arrow tone={tone} />
      </div>
    </div>
  );
}

function Arrow({ tone = 'ok' }) {
  return (
    <svg
      viewBox="0 0 10 6"
      aria-hidden="true"
      className={`h-1.5 w-2.5 ${
        tone === 'bad' ? 'text-red-400' : tone === 'warn' ? 'text-amber-500' : 'text-gray-400'
      }`}
    >
      <path d="M0 0 L5 6 L10 0 Z" fill="currentColor" />
    </svg>
  );
}

// The bracket that makes a branch look like a branch. Each column is split in half: the inner
// halves carry the horizontal rail, and each column's centre drops a stub to its card. Hidden
// on a narrow screen, where the columns stack and a rail would point at nothing.
function FanRail({ n }) {
  if (n < 2) return null;
  return (
    <div className="hidden w-full sm:flex" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="flex min-w-0 flex-1">
          <div className={`h-4 flex-1 ${i === 0 ? '' : 'border-t border-gray-300'}`} />
          <div className={`h-4 flex-1 border-l border-gray-300 ${i === n - 1 ? '' : 'border-t border-gray-300'}`} />
        </div>
      ))}
    </div>
  );
}

export default function LineCallPath({ line }) {
  const routingQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'routing'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}/routing`),
    enabled: Boolean(line.subscriberId && line.number),
  });

  const extQ = useQuery({
    queryKey: ['line', String(line.id), 'extensions'],
    queryFn: () => api.get(`/lines/${line.id}/extensions`),
  });

  // Shared with the Diagnostics rail — same key, so react-query serves both from one read
  // and the two surfaces cannot disagree about whether a phone is up.
  const regsQ = useQuery({
    queryKey: ['sip-registrations'],
    queryFn: () => api.get('/sip-registrations'),
    refetchInterval: 60_000,
  });

  // THE ROUTER'S STATE, fetched only when this line actually has one.
  //
  // `enabled` is the whole safety argument. Calix forbids mass polling, so this must never fire
  // for a line whose phones are Grandstream — which is most of them — and it only knows that once
  // the extensions have loaded. The worker caches per router for five minutes, so this and the
  // subscriber page's Devices tab cost one Calix call between them however often either is opened.
  //
  // SAME QUERY KEY as the subscriber page's card, deliberately: react-query then serves both from
  // one read, and the two surfaces cannot disagree about whether a router is up — the same reason
  // `sip-registrations` is shared with the Diagnostics rail.
  const hasCalix = (extQ.data?.extensions || extQ.data || [])
    .some((e) => e?.deviceSelfProvisioning && e?.deviceFsan);
  const calixQ = useQuery({
    queryKey: ['subscriber', String(line.subscriberId), 'calix-health'],
    queryFn: () => api.get(`/subscribers/${line.subscriberId}/calix-health`),
    enabled: Boolean(line.subscriberId) && hasCalix,
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!line.number) return null;

  const model = callPathModel({
    line,
    calix: calixQ.data || null,
    routing: routingQ.data || null,
    rings: extQ.data?.extensions || [],
    regs: regsQ.data || null,
  });
  const { carrier, host, fan, legs, tail } = model;

  const loading = routingQ.isLoading || extQ.isLoading;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            What happens when someone calls this number
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            The carrier, the host, the extensions it rings and the phone on each — read live
            from {host.name || 'the host'}, not from what Verso intends.
          </p>
        </div>
        {regsQ.data?.cached && <span className="text-[10px] text-gray-400">cached</span>}
      </div>

      {/* Said once, at the top, in the same words the rest of the product uses. Everything
          below then renders as unknown rather than as absent. */}
      {host.error && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {host.name || 'The host'} could not be reached, so what it does with this call and
          whether the phones are registered are not known — not empty. {host.error}
        </p>
      )}
      {routingQ.isError && (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Could not read the call path, so the host and everything after it are unknown:{' '}
          {routingQ.error?.body?.error || routingQ.error?.message}
        </p>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-gray-400">Reading the path…</p>
      ) : (
        <div className="mt-4 flex flex-col items-center">
          {/* ---- The caller, at the carrier. ---- */}
          <Node
            title={carrier.number}
            mono
            wide
            sub={carrier.cnam ? `calls arrive showing “${carrier.cnam}”` : null}
            chips={[
              // `other_profile` is neither green nor red: the call arrives, on a path that is not the
              // host's own. Amber says "look once", which is what it deserves.
              <Chip key="c" tone={CARRIER_TONE[carrier.state] || 'unknown'}>
                {carrier.route ? `carrier route ${carrier.route}` : 'carrier'}
              </Chip>,
            ]}
            tone={carrier.state === 'bad' ? 'bad' : carrier.state === 'unknown' ? 'unknown' : 'ok'}
          />

          <Leg label={carrier.note} tone={CARRIER_TONE[carrier.state] || 'unknown'} />

          {/* ---- The host. ---- */}
          <Node
            title={host.name || 'No PBX host'}
            wide
            sub={host.destination?.raw ? host.destination.raw : null}
            chips={[
              <Chip key="h" tone={host.state === 'ok' ? 'ok' : host.state === 'flow' ? 'ok' : host.state === 'missing' ? 'warn' : 'unknown'}>
                {host.state === 'ok' ? 'inbound route installed'
                  : host.state === 'flow' ? 'routed by call flow'
                    : host.state === 'missing' ? 'no inbound route'
                      : 'route not known'}
              </Chip>,
            ]}
            tone={host.state === 'ok' || host.state === 'flow' ? 'ok' : host.state === 'missing' ? 'warn' : 'unknown'}
          />

          <Leg
            label={host.note}
            tone={host.state === 'ok' || host.state === 'flow' ? 'ok' : host.state === 'missing' ? 'warn' : 'unknown'}
          />

          {/* ---- A flow. A MENU IS OURS NOW; anything else is still the wall it was. ----
              "Verso does not model this" was drawn under every flow, and stopped being true for
              an auto attendant the day Verso started creating them, setting their greeting and
              reading the whole thing back. Saying it anyway told an operator to go and use
              FreePBX for the one thing this page can do (2026-08-27). */}
          {fan.kind === 'flow' && (
            <>
              <Node
                title={fan.flowLabel}
                wide
                sub={fan.flowTarget ? `#${fan.flowTarget} on the host` : null}
                tone={fan.flowKind === 'ivr' ? 'ok' : 'unknown'}
                chips={fan.flowKind === 'ivr'
                  ? []
                  : [<Chip key="f" tone="unknown">Verso does not model this</Chip>]}
              />
              <Leg
                label={fan.flowKind === 'ivr'
                  ? 'callers hear the greeting, then choose'
                  : 'what it does inside is the host’s own call flow'}
                tone={fan.flowKind === 'ivr' ? 'ok' : 'unknown'}
              />
            </>
          )}

          {/* ---- The branch. ---- */}
          {fan.kind === 'ring-group' && legs.length > 0 && (
            <Node
              title={`Ring group ${fan.groupNum || ''}`.trim()}
              wide
              sub={`${legs.length} extensions ring ${fan.strategy || 'together'}${
                fan.ringTime ? `, for ${fan.ringTime} seconds` : ''}`}
              tone={fan.live ? 'ok' : 'unknown'}
              chips={fan.live ? [] : [
                <Chip key="i" tone="unknown">what Verso intends, not read from the host</Chip>,
              ]}
            />
          )}

          {legs.length === 0 ? (
            <p className="mt-3 text-sm text-amber-800">
              This line rings nothing, so a call to the number reaches no phone.
            </p>
          ) : (
            <>
              <FanRail n={legs.length} />

              {/* One extension is most of the fleet, and a lone column stretched to the full
                  card while every node above it was capped — so the ordinary line drew as a
                  wide slab under a narrow one. Match the spine when there is no branch. */}
              <div className={`flex flex-col gap-3 sm:flex-row ${legs.length === 1 ? 'w-full max-w-md' : 'w-full'}`}>
                {legs.map((leg) => (
                  <div key={leg.sipAccountId} className="flex min-w-0 flex-1 flex-col items-center">
                    <Node
                      title={leg.username}
                      mono
                      sub={leg.name}
                      tone={leg.registration === 'online' ? 'ok'
                        : leg.registration === 'offline' ? 'bad' : 'unknown'}
                      chips={[
                        <Chip
                          key="r"
                          tone={leg.registration === 'online' ? 'ok'
                            : leg.registration === 'offline' ? 'bad' : 'unknown'}
                        >
                          {leg.registration === 'online' ? `online${leg.callState ? ` · ${leg.callState}` : ''}`
                            : leg.registration === 'offline' ? 'offline'
                              : 'no answer from the host'}
                        </Chip>,
                        ...(leg.pushed ? [] : [
                          <Chip key="p" tone="warn">never pushed to the PBX</Chip>,
                        ]),
                        ...(leg.primary && legs.length > 1 ? [
                          <Chip key="f" tone="unknown">first</Chip>,
                        ] : []),
                      ]}
                    />

                    <Leg
                      label={leg.device ? 'rings' : null}
                      tone={leg.device ? 'ok' : 'warn'}
                    />

                    {leg.device ? (
                      <Node
                        title={leg.device.label}
                        sub={leg.device.port != null ? `port ${leg.device.port}` : null}
                        // A Calix reports for itself, so its own answer decides the tone. Grey
                        // while the lookup is in flight — "we have not asked yet" is not "down".
                        tone={leg.device.calix
                          ? (leg.device.calix.state === 'online' ? 'ok'
                            : leg.device.calix.state === 'offline' ? 'bad' : 'unknown')
                          : leg.device.selfProvisioning && calixQ.isFetching ? 'unknown'
                            : leg.device.pushed ? 'ok' : 'warn'}
                        chips={[
                          ...(leg.device.pushed ? [] : [
                            <Chip key="d" tone="warn">never given its config</Chip>,
                          ]),
                          ...(leg.device.selfProvisioning && calixQ.isFetching ? [
                            <Chip key="cl" tone="unknown">asking Calix…</Chip>,
                          ] : []),
                          ...(leg.device.calix ? [
                            <Chip
                              key="cs"
                              tone={leg.device.calix.state === 'online' ? 'ok'
                                : leg.device.calix.state === 'offline' ? 'bad' : 'unknown'}
                            >
                              {leg.device.calix.state === 'online' ? 'Calix: online'
                                : leg.device.calix.state === 'offline' ? 'Calix: offline'
                                  : leg.device.calix.state === 'rate_limited' ? 'Calix is rate-limiting — try again shortly'
                                    : 'Calix could not be reached'}
                            </Chip>,
                            // Only when there is something to say. A healthy router with no issues
                            // should not grow a row of empty labels.
                            ...(leg.device.calix.issues ? [
                              <Chip key="ci" tone="warn">
                                {`${leg.device.calix.issues} open issue${leg.device.calix.issues === 1 ? '' : 's'}`}
                              </Chip>,
                            ] : []),
                            ...(leg.device.calix.firmware ? [
                              <Chip key="cf" tone="unknown">{`fw ${leg.device.calix.firmware}`}</Chip>,
                            ] : []),
                          ] : []),
                        ]}
                      />
                    ) : (
                      <Node
                        title="No device"
                        sub="nothing is bound to this extension, so nothing rings"
                        tone="warn"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* ---- Nobody answers. ---- */}
              <Leg
                label={tail.kind === 'voicemail'
                  ? 'if nobody answers'
                  : 'if nobody answers, it rings out'}
                tone={tail.kind === 'voicemail' ? 'ok' : 'unknown'}
              />
              {tail.kind === 'voicemail' ? (
                <Node
                  title={`Mailbox ${tail.mailbox}`}
                  wide
                  // "FreePBX has no mailbox for a group, so the first extension's box takes
                  // the line's messages" — true, and the operator does nothing with it.
                  // Kevin, 2026-08-13, on the same sentence on the Voicemail tab: hide the
                  // details. What matters on a five-phone line is that there is ONE mailbox.
                  sub={legs.length > 1 ? 'one mailbox for the whole number' : null}
                  tone="ok"
                />
              ) : (
                <Node
                  title="Rings out"
                  wide
                  // WHICH KIND OF OFF. "Switched off" is a decision to revisit; "the host has no
                  // mailbox" is a fact about FreePBX, and telling an operator the first when it is
                  // the second sends them to a switch that will not fix it.
                  sub={tail.reason === 'no_mailbox'
                    ? 'the host has no mailbox for this extension'
                    : 'voicemail is off for this line'}
                  tone="unknown"
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
